'use client';

/**
 * Filter bar above the home / landing character grid (design ref Oct 2026):
 * Gender · Style · Sort dropdowns, then category chips filling the row with a
 * More overflow, and View all at the far right. Gender and style drive the
 * shared ExploreFilter context; sort and category belong to the grid.
 */
import { useState } from 'react';

import Link from 'next/link';

import {
  ChevronRight,
  Clock,
  Flame,
  LayoutGrid,
  PawPrint,
  Sparkles,
  User,
} from 'lucide-react';

import {
  ChipRow,
  FilterDropdown,
  FilterStyles,
  GenderGlyph,
  chip,
} from '~/components/catalog/filter-controls';
import { useExploreFiltersOptional } from '~/components/navigation/explore-filter-context';
import { CATEGORIES } from '~/lib/catalog-categories';

export type FeaturedSort = 'newest' | 'popular';

export interface FeaturedView {
  category: string;
  sort: FeaturedSort;
}

const GENDERS = ['Female', 'Male', 'Trans'] as const;
const STYLES = ['Realistic', 'Anime', 'Furry'] as const;

const ICON = 'h-[18px] w-[18px]';
const STYLE_ICONS = {
  Realistic: <User className={ICON} />,
  Anime: <Sparkles className={ICON} />,
  Furry: <PawPrint className={ICON} />,
};

type Menu = 'gender' | 'style' | 'sort' | 'more' | null;

export function FeaturedFilterBar({
  view,
  onChange,
  signedIn,
}: {
  view: FeaturedView;
  onChange: (view: FeaturedView) => void;
  signedIn: boolean;
}) {
  const filters = useExploreFiltersOptional();
  const [open, setOpen] = useState<Menu>(null);
  const toggle = (menu: Exclude<Menu, null>) =>
    setOpen((current) => (current === menu ? null : menu));

  const gender = GENDERS.find((g) => filters?.isFilterSelected(g)) ?? null;
  const style = STYLES.find((s) => filters?.isFilterSelected(s)) ?? null;

  // A group is single-choice: clear the others, then select (or leave empty).
  const setExclusive = (group: readonly string[], label: string | null) => {
    if (!filters) return;
    for (const g of group) {
      if (g !== label && filters.isFilterSelected(g)) filters.toggleFilter(g);
    }
    if (label && !filters.isFilterSelected(label)) filters.toggleFilter(label);
  };

  const categories = CATEGORIES.filter(
    (c) => c.key !== 'favorites' || signedIn,
  );

  return (
    <div className="relative z-30">
      <FilterStyles />
      {open && (
        <div
          className="fixed inset-0 z-20"
          aria-hidden
          onClick={() => setOpen(null)}
        />
      )}
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="grid grid-cols-3 gap-2 md:flex md:flex-shrink-0">
          <div className="md:w-[150px]">
            <FilterDropdown
              icon={
                <GenderGlyph
                  gender={gender ?? 'female'}
                  className="h-4 w-4 text-white/80"
                />
              }
              label={gender ?? 'Gender'}
              active={false}
              open={open === 'gender'}
              onToggle={() => toggle('gender')}
              value={gender ?? 'any'}
              options={[
                {
                  value: 'any',
                  label: 'Any',
                  icon: <LayoutGrid className={ICON} />,
                },
                ...GENDERS.map((g) => ({
                  value: g as string,
                  label: g,
                  icon: <GenderGlyph gender={g} className={ICON} />,
                })),
              ]}
              onSelect={(v) => {
                setExclusive(GENDERS, v === 'any' ? null : v);
                setOpen(null);
              }}
            />
          </div>
          <div className="md:w-[150px]">
            <FilterDropdown
              icon={
                style ? (
                  <span className="text-white/80">{STYLE_ICONS[style]}</span>
                ) : (
                  <Sparkles className="h-4 w-4 text-white/80" />
                )
              }
              label={style ?? 'Style'}
              active={false}
              open={open === 'style'}
              onToggle={() => toggle('style')}
              value={style ?? 'any'}
              options={[
                {
                  value: 'any',
                  label: 'Any',
                  icon: <LayoutGrid className={ICON} />,
                },
                ...STYLES.map((s) => ({
                  value: s as string,
                  label: s,
                  icon: STYLE_ICONS[s],
                })),
              ]}
              onSelect={(v) => {
                setExclusive(STYLES, v === 'any' ? null : v);
                setOpen(null);
              }}
            />
          </div>
          <div className="md:w-[150px]">
            <FilterDropdown
              icon={
                view.sort === 'popular' ? (
                  <Flame className="h-4 w-4 text-white/80" />
                ) : (
                  <Clock className="h-4 w-4 text-white/80" />
                )
              }
              label={view.sort === 'popular' ? 'Popular' : 'Newest'}
              active={false}
              open={open === 'sort'}
              onToggle={() => toggle('sort')}
              value={view.sort}
              align="right"
              options={[
                {
                  value: 'newest',
                  label: 'Newest',
                  icon: <Clock className={ICON} />,
                },
                {
                  value: 'popular',
                  label: 'Popular',
                  icon: <Flame className={ICON} />,
                },
              ]}
              onSelect={(sort) => {
                onChange({ ...view, sort });
                setOpen(null);
              }}
            />
          </div>
        </div>

        <div className="min-w-0 flex-1 md:pl-2">
          <ChipRow
            categories={categories}
            activeKey={view.category}
            moreOpen={open === 'more'}
            onToggleMore={() => toggle('more')}
            onSelect={(category) => {
              onChange({ ...view, category });
              setOpen(null);
            }}
            trailing={
              // Desktop only: on phones the row keeps All / Popular / New / More.
              <span className="hidden md:inline-flex">
                <Link
                  href="/catalog"
                  style={chip()}
                  className="cat-chip border border-white/15 font-semibold text-white"
                >
                  View all
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Link>
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
}
